import { useLocalObservable } from 'mobx-react-lite';
import { RefObject } from 'react';

import { IImageCropperHandlers } from '@gem/components';
import { uploader } from '@gem/modules/file-upload';
import { useTranslation } from '@gem/modules/i18n';
import { RemoteImageObject } from '@gem/modules/remote-storage';

import Tasks from 'APP/Tasks';

interface IImageCropperPopupPresenterParams {
  onChange(url: string): void;
  onClose(): void;
  cropperRef: RefObject<IImageCropperHandlers>;
}

export interface IImageCropperPopupPresenter {
  isLoading: boolean;
  onError(): void;
  onUploadError(): void;
  handleUploadImage(): Promise<void>;
}

export function useImageCropperPopupPresenter({
  onChange,
  onClose,
  cropperRef,
}: IImageCropperPopupPresenterParams): IImageCropperPopupPresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IImageCropperPopupPresenter>(() => ({
    isLoading: false,

    onError(): void {
      Tasks.app.showAlert(t('common_somethings_wrong_error'));
      onClose();
    },

    onUploadError(): void {
      Tasks.app.showAlert(t('common_somethings_wrong_error'));
      presenter.isLoading = false;
    },

    async handleUploadImage(): Promise<void> {
      if (!cropperRef.current) {
        return;
      }

      presenter.isLoading = true;

      try {
        const croppedImage = await cropperRef.current.getCroppedImage();
        const url = await uploader.uploadBase64(croppedImage);

        const preloadImage = new RemoteImageObject();

        preloadImage.onload = (): void => {
          onChange(url);
          onClose();
        };
        preloadImage.onerror = (): void => {
          presenter.onUploadError();
        };
        preloadImage.src = url;
      } catch {
        presenter.onUploadError();
      }
    },
  }));

  return presenter;
}
